/**
 * Users Index Page
 *
 * Main page for tenant user management.
 * Allows tenant admins to view, invite, edit, and remove users.
 *
 * @fileoverview Tenant users management page
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  Center,
  Spinner,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdPersonAdd } from 'react-icons/md';

import {
  AppShell,
  Sidebar,
  Header,
} from '../../components/layout';
import { getNavItems } from '../../components/layout/navItems';
import { ProfileMenu, AdminTenantSearch } from '../../components/app';
import {
  UserStats,
  UsersTable,
  UserInviteModal,
  UserEditModal,
  RemoveUserModal,
  DeactivateUserModal,
} from '../../components/users';

import { useUser } from '../../Context/userContext';
import { useAuth } from '../../hooks/useAuth';
import {
  useTenantUsers,
  useTenantUserStats,
  useTenantUserActions,
} from '../../hooks/useTenantUsers';
import type { TenantUser, InviteTenantUserInput, UpdateTenantUserInput } from '../../types/users';
import { toUserMessage } from '../../lib/apiError';

/**
 * Logo component for header
 */
const Logo: React.FC = () => {
  const textColor = useColorModeValue('xacto.electricCyan', 'xacto.electricCyan');

  return (
    <HStack spacing={2}>
      <Box
        w="32px"
        h="32px"
        bg="xacto.electricCyan"
        borderRadius="md"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="xacto.deepNavy" fontWeight="bold" fontSize="lg">
          X
        </Text>
      </Box>
      <Text
        fontWeight="bold"
        fontSize="lg"
        color={textColor}
        display={{ base: 'none', md: 'block' }}
      >
        Xacto
      </Text>
    </HStack>
  );
};

/**
 * Users page content component
 */
const UsersContent: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, selectedTenant, isLoading } = useUser();

  // Debug logging
  console.log('[UsersContent] Render state:', {
    isLoading,
    user: user?.email,
    selectedTenant: selectedTenant?.name,
    tenantRole: selectedTenant?.tenantRole,
    isOwner: selectedTenant?.isOwner,
  });

  // Check if user is tenant admin
  const isAdmin = user?.globalRole === 'ADMIN';
  const isTenantAdmin = isAdmin || selectedTenant?.isOwner || selectedTenant?.tenantRole === 'TENANT_ADMIN';

  console.log('[UsersContent] Auth check:', { isAdmin, isTenantAdmin });

  // Get nav items
  const navItems = getNavItems(isAdmin, isTenantAdmin);

  // Fetch users and stats (hooks must be called unconditionally)
  const { users, loading: usersLoading, refetch: refetchUsers } = useTenantUsers(selectedTenant?.id);
  const { stats, loading: statsLoading, refetch: refetchStats } = useTenantUserStats(selectedTenant?.id);
  const { inviteUser, updateUser, removeUser, deactivateUser, reactivateUser, loading: actionLoading, error: actionError } = useTenantUserActions(selectedTenant?.id);

  // Modal states
  const inviteModal = useDisclosure();
  const editModal = useDisclosure();
  const removeModal = useDisclosure();
  const deactivateModal = useDisclosure();

  // Selected user for edit/remove modals
  const [selectedUser, setSelectedUser] = useState<TenantUser | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);

  // Clear modal error when modals close
  useEffect(() => {
    if (!inviteModal.isOpen && !editModal.isOpen && !removeModal.isOpen && !deactivateModal.isOpen) {
      setModalError(null);
    }
  }, [inviteModal.isOpen, editModal.isOpen, removeModal.isOpen, deactivateModal.isOpen]);

  /**
   * Handle sidebar navigation
   */
  const handleNavigate = useCallback((path: string): void => {
    navigate(path);
  }, [navigate]);

  /**
   * Handle invite user
   */
  const handleInviteUser = useCallback(async (data: InviteTenantUserInput) => {
    try {
      setModalError(null);
      await inviteUser(data);
      toast({
        title: 'Invite sent',
        description: `Invitation sent to ${data.email}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      inviteModal.onClose();
      refetchStats();
    } catch (err) {
      setModalError(toUserMessage(err, "We couldn't send the invite. Please try again."));
    }
  }, [inviteUser, inviteModal, refetchStats, toast]);

  /**
   * Handle edit user
   */
  const handleEditUser = useCallback((userToEdit: TenantUser) => {
    setSelectedUser(userToEdit);
    editModal.onOpen();
  }, [editModal]);

  /**
   * Handle update user role
   */
  const handleUpdateUser = useCallback(async (userId: string, data: UpdateTenantUserInput) => {
    try {
      setModalError(null);
      await updateUser(userId, data);
      toast({
        title: 'User updated',
        description: 'User role has been updated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      editModal.onClose();
      setSelectedUser(null);
      refetchUsers();
      refetchStats();
    } catch (err) {
      setModalError(toUserMessage(err, "We couldn't update the user. Please try again."));
    }
  }, [updateUser, editModal, refetchUsers, refetchStats, toast]);

  /**
   * Handle deactivate user
   */
  const handleDeactivateUser = useCallback((userToDeactivate: TenantUser) => {
    setSelectedUser(userToDeactivate);
    deactivateModal.onOpen();
  }, [deactivateModal]);

  /**
   * Confirm deactivate user
   */
  const confirmDeactivateUser = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await deactivateUser(selectedUser.userId);
      toast({
        title: 'User deactivated',
        description: 'User has been deactivated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      deactivateModal.onClose();
      setSelectedUser(null);
      refetchUsers();
      refetchStats();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't deactivate the user. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedUser, deactivateUser, deactivateModal, refetchUsers, refetchStats, toast]);

  /**
   * Handle reactivate user
   */
  const handleReactivateUser = useCallback(async (userToReactivate: TenantUser) => {
    try {
      await reactivateUser(userToReactivate.userId);
      toast({
        title: 'User reactivated',
        description: 'User has been reactivated',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      refetchUsers();
      refetchStats();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't reactivate the user. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [reactivateUser, refetchUsers, refetchStats, toast]);

  /**
   * Handle remove user
   */
  const handleRemoveUser = useCallback((userToRemove: TenantUser) => {
    setSelectedUser(userToRemove);
    removeModal.onOpen();
  }, [removeModal]);

  /**
   * Confirm remove user
   */
  const confirmRemoveUser = useCallback(async () => {
    if (!selectedUser) return;
    try {
      await removeUser(selectedUser.userId);
      toast({
        title: 'User removed',
        description: 'User has been removed from the tenant',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      removeModal.onClose();
      setSelectedUser(null);
      refetchUsers();
      refetchStats();
    } catch (err) {
      toast({
        title: 'Something went wrong',
        description: toUserMessage(err, "We couldn't remove the user. Please try again."),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [selectedUser, removeUser, removeModal, refetchUsers, refetchStats, toast]);

  // Early returns AFTER all hooks (React Rules of Hooks compliance)
  // Show loading state while fetching user data
  if (isLoading) {
    console.log('[UsersContent] RETURN: Loading spinner');
    return (
      <Center h="100vh" bg="bg.primary">
        <VStack spacing={4}>
          <Spinner size="xl" color="accent.primary" thickness="4px" />
          <Text color="text.secondary">Loading...</Text>
        </VStack>
      </Center>
    );
  }

  // Redirect to home if no tenant selected
  if (!selectedTenant) {
    console.log('[UsersContent] RETURN: No tenant - redirecting');
    return <Navigate to="/home" replace />;
  }

  // Redirect to home if not tenant admin
  if (!isTenantAdmin) {
    console.log('[UsersContent] RETURN: Not tenant admin - redirecting');
    return <Navigate to="/home" replace />;
  }

  console.log('[UsersContent] Rendering main content for tenant:', selectedTenant?.name);
  console.log('[UsersContent] Users hook state:', { users: users?.length, usersLoading });
  console.log('[UsersContent] Stats hook state:', { stats, statsLoading });

  return (
    <>
      <AppShell
        header={
          <Header
            logo={<Logo />}
            title="Xacto Financial Dashboard"
            actions={<AdminTenantSearch />}
            userMenu={<ProfileMenu />}
          />
        }
        sidebar={
          <Sidebar
            items={navItems}
            currentPath="/users"
            onNavigate={handleNavigate}
          />
        }
      >
        <VStack spacing={6} align="stretch">
          {/* Page header */}
          <HStack justify="space-between" align="center">
            <Box>
              <Heading size="lg" color="text.primary" mb={1}>
                Users
              </Heading>
              <Text color="text.secondary">
                Manage users for {selectedTenant?.name || 'your tenant'}
              </Text>
            </Box>
            <Button
              leftIcon={<MdPersonAdd />}
              colorScheme="blue"
              onClick={inviteModal.onOpen}
            >
              Invite User
            </Button>
          </HStack>

          {/* Stats */}
          <UserStats stats={stats} loading={statsLoading} />

          {/* Users table */}
          <UsersTable
            users={users}
            loading={usersLoading}
            currentUserId={user?.id}
            onEdit={handleEditUser}
            onDeactivate={handleDeactivateUser}
            onReactivate={handleReactivateUser}
            onRemove={handleRemoveUser}
          />
        </VStack>
      </AppShell>

      {/* Modals */}
      <UserInviteModal
        isOpen={inviteModal.isOpen}
        onClose={inviteModal.onClose}
        onSubmit={handleInviteUser}
        loading={actionLoading}
        error={modalError}
      />

      <UserEditModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.onClose();
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSubmit={handleUpdateUser}
        loading={actionLoading}
        error={modalError}
      />

      <RemoveUserModal
        isOpen={removeModal.isOpen}
        onClose={() => {
          removeModal.onClose();
          setSelectedUser(null);
        }}
        user={selectedUser}
        onConfirm={confirmRemoveUser}
        loading={actionLoading}
      />

      <DeactivateUserModal
        isOpen={deactivateModal.isOpen}
        onClose={() => {
          deactivateModal.onClose();
          setSelectedUser(null);
        }}
        user={selectedUser}
        onConfirm={confirmDeactivateUser}
        loading={actionLoading}
      />
    </>
  );
};

/**
 * Users Index Page
 *
 * Main page for tenant user management using UserProvider from AppRouter context.
 */
export const UsersIndex: React.FC = () => {
  return <UsersContent />;
};

export default UsersIndex;
